/**
 * App entry (HANDOFF §6 Phase 2 integration).
 *
 * Wires the pure deterministic sim to the Canvas renderer, the DOM HUD / card
 * hand / overlays, and pointer input — all through the fixed-timestep loop.
 *
 * Architecture (§4): the simulation is the single source of truth. Input EMITS
 * Actions → `reduce`; the loop ticks the sim at a fixed dt; render READS state
 * only. The renderer owns the field→screen transform and hands `screenToField`
 * to the controls so aim is computed in field space.
 */
import '@fontsource/press-start-2p';
import '../styles/tokens.css';
import './shell.css';
import '../ui/ui.css';
import { registerPwa } from './pwa.ts';
import { createRenderer } from '../render/canvas.ts';
import { loadSprites } from '../render/sprites.ts';
import { connectMatch, findMatch, matchUrl, type NetMatch, type Matchmaking } from './net.ts';
import { createHud } from '../render/hud.ts';
import { attachControls } from '../input/controls.ts';
import { createCardHand, createStartScreen, createEndScreen, createDeckBuilder, createSettingsPanel, createTutorial, createPackShop, createCollection } from '../ui/index.ts';
import type { DeckSelection } from '../ui/index.ts';
import { createLoop } from './loop.ts';
import { createFeedback, deriveEvents } from './feedback.ts';
import type { GameEvent } from './feedback.ts';
import { createMusic } from './music.ts';
import { initAnalytics, track, captureError } from './analytics.ts';
import { loadEconomy, ownedIds, onEconomyChange, getEconomy, openPack, grantReward, getToken, loadProfile } from './economy.ts';
import { loadSettings, saveSettings } from './settings.ts';
import { createInitialState, reduce, advanceSwing, idleSwing } from '../sim/game.ts';
import { MATCH_DURATION_SECONDS, boxForPower } from '../sim/constants.ts';
import { CLUBS } from '../sim/clubs.ts';
import { DEFENSE_CARDS } from '../sim/cards.ts';
import { createRng, timeSeed } from '../sim/rng.ts';
import type { Action, Difficulty, GameState, Rng, SwingState } from '../sim/types.ts';

/** Build the play stage: a positioned shell with the full-bleed field canvas. */
function mountStage(root: HTMLElement): { stage: HTMLElement; canvas: HTMLCanvasElement } {
  root.replaceChildren();
  const stage = document.createElement('main');
  stage.className = 'app-shell';
  const fieldWrap = document.createElement('div');
  fieldWrap.className = 'field-wrap';
  const canvas = document.createElement('canvas');
  canvas.className = 'field';
  canvas.id = 'field';
  fieldWrap.appendChild(canvas);
  stage.appendChild(fieldWrap);
  root.appendChild(stage);
  return { stage, canvas };
}

function bootstrap(): void {
  const root = document.querySelector<HTMLElement>('#app');
  if (!root) throw new Error('#app root not found');

  initAnalytics(); // GameAnalytics + Sentry (no-ops without keys; GA auto-starts the session)

  const { stage, canvas } = mountStage(root);

  // --- Simulation: the single source of truth (§4). ------------------------
  let rng: Rng = createRng(timeSeed());
  let state: GameState = createInitialState(rng, 'normal', timeSeed());
  let lastDifficulty: Difficulty = 'normal';

  // Feedback (sound + haptics): derive feel events by diffing state — the sim stays pure.
  const feedback = createFeedback();
  // Music: a procedural synth track on the SAME audio graph, adaptive to the match.
  const music = createMusic();
  // Persisted player settings (audio volumes/mute, haptics, handedness).
  const settings = loadSettings();

  // Online match connection (null = local/solo: the client runs the sim itself, as before).
  let online: NetMatch | null = null;
  // Active matchmaking search (queued for an opponent), or null.
  let matchmaking: Matchmaking | null = null;
  // Online-session epoch: bumped whenever we DELIBERATELY leave (new mode, rematch, match over), so
  // stale socket callbacks — a close event arriving after we've moved on — can tell they're obsolete
  // and neither clobber the new screen nor trigger a reconnect to a match we left.
  let netEpoch = 0;
  /** Deliberately leave any online session (live match and/or matchmaking queue). */
  const leaveOnline = (): void => {
    netEpoch++;
    if (online !== null) {
      online.close();
      online = null;
    }
    if (matchmaking !== null) {
      matchmaking.cancel();
      matchmaking = null;
    }
  };
  // The mode of the current online match ('ranked' rates; 'casual' doesn't).
  let onlineMode: 'ranked' | 'casual' = 'casual';
  // CLIENT PREDICTION (online): YOUR swing meter runs locally for zero-latency feel; on lock we send
  // the value you actually saw, and the server fires the shot with it. The opponent + scores + balls
  // stay server-authoritative. In solo this is unused (the sim is already local).
  let localSwing: SwingState = idleSwing;
  // Predicted BALL flight (online): on fire we run YOUR shot locally so it launches instantly; the
  // server's authoritative ball (deterministic ⇒ identical path) takes over once the prediction ends.
  let predicted: GameState | null = null;
  // Range mode (endless local practice): a bank of balls that each shot spends. Refill = a future
  // rewarded ad ("tap for more balls"); for now the button just tops it up.
  const RANGE_BANK = 30;
  let rangeBalls = 0;
  // First-run tutorial: runs on top of a range session with guided coach prompts (no ball gate).
  let inTutorial = false;

  /** Music intensity from match state: a calm bed in menus, then a GRADUAL climb across the whole
   *  match clock so it gets steadily more intense / dire as time runs out (arp enters mid-match, a
   *  drive pulse fades in, and it goes frantic in the final stretch). */
  const intensityFor = (s: GameState): number => {
    if (s.phase === 'playoff') return 1; // sudden death — peak tension
    if (s.phase !== 'playing') return 0.32; // start / deck / end — just the pad + bass
    const elapsed = 1 - Math.max(0, Math.min(1, s.clock / MATCH_DURATION_SECONDS)); // 0 → 1 over the match
    return 0.4 + elapsed * 0.58; // steady build 0.40 → 0.98 from tee-off to the buzzer
  };

  /** Side-chain: pick the deepest duck the batch of SFX warrants so the music dips out of their
   *  way (cheers/steals duck most, a splash less, your own strike just a touch). */
  const duckMusic = (events: readonly GameEvent[]): void => {
    let depth = 0;
    let release = 0.25;
    for (const e of events) {
      if (e.kind === 'scored') {
        if (e.result === 'ace' || e.result === 'eagle') {
          depth = Math.max(depth, 0.7);
          release = Math.max(release, 0.85);
        } else if (e.result === 'birdie') {
          depth = Math.max(depth, 0.5);
          release = Math.max(release, 0.6);
        } else depth = Math.max(depth, 0.26);
      } else if (e.kind === 'gopher') {
        depth = Math.max(depth, 0.55);
        release = Math.max(release, 0.55);
      } else if (e.kind === 'water') {
        depth = Math.max(depth, 0.38);
        release = Math.max(release, 0.4);
      } else if (e.kind === 'strike' && e.mine) depth = Math.max(depth, 0.2);
    }
    if (depth > 0) music.duck(depth, release);
  };

  /** What the renderer + controls SEE: online, YOUR swing + YOUR fired ball are locally-predicted
   *  (zero latency); the opponent, scores, clock + opponent balls stay the server's authoritative
   *  state. (Your predicted ball is deterministic, so the server's copy lands on the same spot.) */
  const viewState = (): GameState => {
    if (online === null) return state;
    const base: GameState = { ...state, swing: localSwing };
    if (predicted === null) return base;
    return { ...base, balls: [...state.balls.filter((b) => b.side !== 'you'), ...predicted.balls.filter((b) => b.side === 'you')] };
  };

  /** Online: drive the local swing meter optimistically + return the action to SEND. Lock actions
   *  carry the meter value the player actually saw, so the server fires the authoritative shot with
   *  it (the meter is a client-trusted skill input; the server still owns + validates the physics). */
  const predictSwing = (action: Action): Action => {
    switch (action.type) {
      case 'PlayOffense': {
        const club = state.you.offenseHand[action.cardIdx];
        // predict only an affordable tee-up (else the server rejects → a stuck local swing)
        if (club && club.cost <= Math.floor(state.you.energy) && (localSwing.phase === 'idle' || localSwing.phase === 'aim')) {
          localSwing = { phase: 'aim', side: 'you', clubIdx: action.cardIdx, meter: 0, meterDir: 1, aimAngle: null, backswingMeter: null, impactMeter: null };
        }
        return action;
      }
      case 'Aim':
        if (localSwing.phase === 'aim') localSwing = { ...localSwing, aimAngle: action.angle };
        return action;
      case 'StartSwing':
        if (localSwing.phase === 'aim' && localSwing.aimAngle !== null) localSwing = { ...localSwing, phase: 'backswing', meter: 0, meterDir: 1 };
        return action;
      case 'LockBackswing':
        if (localSwing.phase === 'backswing') {
          const meter = localSwing.meter;
          localSwing = { ...localSwing, phase: 'impact', meterDir: -1, backswingMeter: meter };
          return { type: 'LockBackswing', meter };
        }
        return action;
      case 'LockImpact':
        if (localSwing.phase === 'impact') {
          const meter = localSwing.meter;
          // Fire YOUR shot locally so the ball launches with zero latency (deterministic ⇒ the
          // server's authoritative ball lands on the same spot). EXCEPT from a ROUGHED tee: the
          // rough spray is an rng draw, and the client + server streams differ, so a predicted
          // rough ball would visibly land somewhere else — let the server ball lead there.
          const club = localSwing.clubIdx !== null ? state.you.offenseHand[localSwing.clubIdx] : undefined;
          const roughed = club !== undefined && state.you.tees[boxForPower(club.power)].roughRemaining > 0;
          if (!roughed) {
            predicted = reduce({ ...state, swing: localSwing }, { type: 'LockImpact', meter }, rng, 'you');
          }
          feedback.emit([{ kind: 'strike', mine: true }]); // instant thwack to match the instant launch
          localSwing = idleSwing;
          return { type: 'LockImpact', meter };
        }
        return action;
      default:
        return action;
    }
  };

  const dispatch = (action: Action): void => {
    if (online !== null) {
      online.send(predictSwing(action));
      return;
    }
    // Range: a swing costs a ball; with an empty bank you can't tee up another. (The tutorial is
    // exempt — it should never run dry mid-lesson.)
    if (state.mode === 'range' && !inTutorial && rangeBalls <= 0 && action.type === 'PlayOffense') return;
    const prev = state;
    state = reduce(state, action, rng);
    const events = deriveEvents(prev, state);
    feedback.emit(events);
    duckMusic(events);
    if (state.mode === 'range' && state.you.shots > prev.you.shots) {
      rangeBalls = Math.max(0, rangeBalls - (state.you.shots - prev.you.shots));
      updateRangeHud();
    }
  };
  /** Adopt an authoritative state from the server (online): diff it for feel + music ducking. The
   *  locally-predicted swing is left alone (client owns its own meter). */
  const adoptServerState = (next: GameState): void => {
    const prev = state;
    state = next;
    // We already played YOUR strike locally on fire (the instant thwack) — drop the server's
    // duplicate of it so it doesn't double up. The opponent's strike (mine:false) still plays.
    const events = deriveEvents(prev, state).filter((e) => !(e.kind === 'strike' && e.mine));
    feedback.emit(events);
    duckMusic(events);
  };
  const getState = (): GameState => viewState();

  // --- View + chrome: read state only / emit actions only. -----------------
  const renderer = createRenderer(canvas);
  // Load the real pixel-art sprites (async). Until they land — or if they fail / there's no DOM —
  // the renderer draws procedurally, so nothing blocks on the assets.
  void loadSprites()
    .then((s) => renderer.setSprites(s))
    .catch((err) => captureError(err));
  const hud = createHud(stage);
  const hand = createCardHand(stage, { dispatch, getState });

  // --- Settings: a quick field MUTE (mid-match) + a full pop-up reached from the start screen. ----
  const muteBtn = document.createElement('button');
  muteBtn.type = 'button';
  muteBtn.setAttribute('aria-label', 'Mute audio');
  muteBtn.style.cssText =
    'position:absolute;left:calc(8px + env(safe-area-inset-left));bottom:calc(70px + env(safe-area-inset-bottom));' +
    'width:30px;height:30px;border-radius:50%;border:1px solid #2c4255;background:rgba(8,16,22,.6);color:#f4efe2;' +
    'font-size:15px;line-height:1;display:flex;align-items:center;justify-content:center;z-index:25;pointer-events:auto;' +
    'cursor:pointer;-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);';
  stage.appendChild(muteBtn);

  /** Apply the current settings to the audio engine + renderer, refresh the mute icon, and persist. */
  function applySettings(): void {
    feedback.setMuted(settings.muted);
    feedback.setSfxVolume(settings.sfxVol);
    feedback.setHaptics(settings.haptics);
    music.setMuted(settings.muted);
    music.setVolume(settings.musicVol);
    renderer.setLeftHanded(settings.handedness === 'left');
    muteBtn.textContent = settings.muted ? '🔇' : '🔊';
    saveSettings(settings);
  }
  muteBtn.addEventListener('click', () => {
    settings.muted = !settings.muted;
    applySettings();
  });
  const settingsPanel = createSettingsPanel(stage, {
    get: () => settings,
    onChange: (patch) => {
      Object.assign(settings, patch);
      applySettings();
    },
    onReplayTutorial: () => {
      settingsPanel.close();
      startTutorial();
    },
  });
  applySettings(); // initial apply (handedness + mute icon now; audio takes effect once unlocked)

  // Card-pack shop (the economy spend loop): buy a pack → the server rolls → reveal the cards.
  const packShop = createPackShop(stage, {
    cost: 100,
    coins: () => getEconomy()?.coins ?? 0,
    open: () => openPack('standard'),
  });

  // Collection gallery: the whole roster, owned in colour (with shards) + unowned locked.
  const collection = createCollection(stage, {
    offense: CLUBS,
    defense: DEFENSE_CARDS,
    owned: () => {
      const e = getEconomy();
      return e ? new Map(e.cards.map((c) => [c.id, c])) : null;
    },
  });

  // Flow: start screen → deck builder → match. `startMatch` is hoisted (defined at the bottom).
  let pendingDifficulty: Difficulty = 'normal';
  const startScreen = createStartScreen(stage, {
    onStart: (difficulty) => {
      pendingDifficulty = difficulty;
      startScreen.hide();
      deckBuilder.show();
    },
    onSettings: () => settingsPanel.open(),
    onPlayOnline: (mode) => startOnline(mode),
    onPlayRange: () => startRange(),
    onShop: () => packShop.open(),
    onCollection: () => collection.open(),
  });
  const deckBuilder = createDeckBuilder(stage, {
    offense: CLUBS,
    defense: DEFENSE_CARDS,
    ownedIds, // gate to the player's collection (null/offline ⇒ full roster)
    onPlay: (deck) => startMatch(pendingDifficulty, deck),
    onBack: () => {
      deckBuilder.hide();
      startScreen.show();
    },
  });

  // Economy: pull the wallet + collection, then reflect it into the wallet HUD + deck-builder locks.
  // Degrades silently offline (the deck builder falls back to the full roster).
  onEconomyChange((eco) => {
    if (eco) startScreen.setWallet(eco.coins, eco.gems);
    deckBuilder.refresh();
  });
  void loadEconomy();
  // Ranked profile: show the rating on the start screen (refetched after a ranked match settles).
  const refreshRating = (): void => {
    void loadProfile().then((p) => {
      if (p) startScreen.setRating(p.rating);
    });
  };
  refreshRating();
  // Rematch returns to the DECK BUILDER (not straight into a match) so you rebuild your deck each
  // time — it reopens pre-filled with your last picks (the builder retains them) to tweak or replay.
  const endScreen = createEndScreen(stage, {
    onRematch: () => {
      endScreen.hide();
      pendingDifficulty = lastDifficulty; // keep the difficulty you last played
      deckBuilder.show();
    },
  });

  // First-run tutorial (FTUE): a coach overlay that watches the swing state and guides the first shots.
  const tutorial = createTutorial(stage, {
    onFinish: (completed) => {
      inTutorial = false;
      settings.tutorialDone = true;
      saveSettings(settings);
      track(completed ? 'tutorial:complete' : 'tutorial:skip');
      updateRangeHud();
      // Completed → the overlay clears and the easy match plays on as their first real game.
      // Skipped → abandon it and go to the menu.
      if (!completed) startScreen.show();
    },
  });

  const detachControls = attachControls(canvas, {
    dispatch,
    getState,
    // The renderer's screenToField returns a POOLED Vec2 (zero-alloc); the controls
    // retain the drag-start point across later calls, so copy into a fresh object
    // here or the start/end points alias and a real drag reads as zero-length.
    screenToField: (clientX, clientY) => {
      const p = renderer.screenToField(clientX, clientY);
      return { x: p.x, y: p.y };
    },
  });

  // --- Fixed-timestep loop (§4.2): sim steps at FIXED_DT, render interpolates.
  let endShown = false;
  const loop = createLoop({
    // Online: the SERVER ticks the authoritative sim. We only advance YOUR predicted meter locally
    // (zero-latency feel); the server's broadcasts drive everything else.
    step: (dt) => {
      if (online === null) {
        if (matchmaking === null) dispatch({ type: 'Tick', dt }); // not searching → run the local sim
        return;
      }
      localSwing = advanceSwing(localSwing, dt);
      if (predicted !== null) {
        predicted = reduce(predicted, { type: 'Tick', dt }, rng); // fly YOUR predicted ball locally
        if (!predicted.balls.some((b) => b.side === 'you')) predicted = null; // shot done → server takes over
      }
    },
    render: (alpha) => {
      const view = viewState();
      renderer.render(view, alpha);
      hud.update(view);
      hand.update(view);
      if (inTutorial) tutorial.update(view);
      music.setIntensity(intensityFor(state));
      if (state.phase === 'final' && !endShown) {
        endShown = true;
        const mode = online !== null ? 'online' : 'practice';
        const result = state.winner === 'you' ? 'win' : state.winner === 'rival' ? 'loss' : 'tie';
        track(`match:end:${mode}:${result}`);
        endScreen.show(state);
        if (state.winner === 'you') {
          const before = getEconomy()?.coins ?? 0;
          if (online !== null) {
            // ONLINE: the MatchRoom pays the verified win server-side — never a client claim.
            // Refetch the wallet shortly and reflect the actual delta on the end screen.
            setTimeout(() => {
              void loadEconomy().then((eco) => {
                if (eco && eco.coins > before) endScreen.setReward(`🪙 +${eco.coins - before} earned`);
              });
            }, 1500);
          } else {
            // OFFLINE practice: claim the win (the server throttles this path against farming).
            void grantReward('win').then((total) => {
              if (total !== null && total > before) endScreen.setReward(`🪙 +${total - before} earned`);
            });
          }
        }
        // Ranked online match → the server Elo-updates both ratings; refetch ours once it settles.
        if (online !== null && onlineMode === 'ranked') setTimeout(refreshRating, 1500);
        music.setPlaying(false); // match decided — fade the music out behind the end screen.
        leaveOnline(); // deliberate: the finished match's socket events are now stale
        showWaiting(null);
        loop.stop(); // no more sim; the final frame stays painted.
      }
    },
  });

  // "Waiting for opponent" / connection overlay (the online lobby — online never starts vs an AI).
  const waitingEl = document.createElement('div');
  waitingEl.style.cssText =
    'position:absolute;inset:0;z-index:55;display:none;align-items:center;justify-content:center;' +
    'background:rgba(6,12,18,.82);-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);' +
    'color:#f4efe2;font-family:var(--font-pixel,monospace);font-size:11px;letter-spacing:.04em;' +
    'white-space:pre-line;text-align:center;padding:24px;line-height:1.9;pointer-events:none;';
  stage.appendChild(waitingEl);
  function showWaiting(message: string | null): void {
    if (message === null) {
      waitingEl.style.display = 'none';
      return;
    }
    waitingEl.textContent = message;
    waitingEl.style.display = 'flex';
  }

  // Range HUD: the ball bank readout + a refill button (placeholder for the "tap for more balls" ad).
  const rangeHud = document.createElement('div');
  rangeHud.style.cssText =
    'position:absolute;top:calc(58px + env(safe-area-inset-top));left:50%;transform:translateX(-50%);' +
    'z-index:26;display:none;font-family:var(--font-pixel,monospace);font-size:11px;color:#f4efe2;' +
    'background:rgba(8,16,22,.6);padding:6px 12px;border-radius:8px;border:1px solid #2c4255;' +
    '-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);text-align:center;pointer-events:none;';
  stage.appendChild(rangeHud);
  const refillBtn = document.createElement('button');
  refillBtn.type = 'button';
  refillBtn.textContent = `Refill balls (+${RANGE_BANK})`;
  refillBtn.style.cssText =
    'position:absolute;left:50%;bottom:calc(84px + env(safe-area-inset-bottom));transform:translateX(-50%);' +
    'z-index:26;display:none;font-family:var(--font-pixel,monospace);font-size:9px;color:#1a1205;cursor:pointer;' +
    'background:var(--gold,#f2c14e);border:0;border-radius:999px;padding:9px 16px;pointer-events:auto;';
  refillBtn.addEventListener('click', () => {
    rangeBalls = RANGE_BANK;
    updateRangeHud();
  });
  stage.appendChild(refillBtn);
  function updateRangeHud(): void {
    const inRange = state.mode === 'range' && online === null && !inTutorial;
    rangeHud.style.display = inRange ? 'block' : 'none';
    rangeHud.textContent = `⛳ BALLS  ${rangeBalls}`;
    refillBtn.style.display = inRange && rangeBalls <= 0 ? 'block' : 'none';
  }

  // Browsers block audio until a user gesture — unlock + start music on the first interaction.
  window.addEventListener(
    'pointerdown',
    () => {
      feedback.unlock();
      music.start(feedback.audioGraph());
      applySettings(); // push volumes/mute/haptics into the now-live audio graph
    },
    { once: true },
  );

  /** Start an ONLINE match: connect to the authoritative server, render its broadcast state, send
   *  our taps. The server runs the sim + the per-client perspective. (M0.3c — fixed match id `test`
   *  while the rival backfills with AI until a 2nd human joins; real matchmaking is M0.4.) */
  function startOnline(mode: 'ranked' | 'casual'): void {
    onlineMode = mode;
    feedback.unlock();
    leaveOnline();
    const epoch = netEpoch; // this session; stale socket callbacks are ignored against it
    state = createInitialState(rng, 'normal', timeSeed()); // placeholder behind the search overlay
    localSwing = idleSwing; // reset the prediction
    predicted = null;
    endShown = false;
    endScreen.hide();
    startScreen.hide();
    deckBuilder.hide();
    updateRangeHud();
    music.start(feedback.audioGraph());
    renderer.resize();
    showWaiting(`Searching for a ${mode} opponent…`);
    track(`online:search:${mode}`);
    // Queue for an opponent in this mode; once paired, join the fresh match the matchmaker hands us.
    matchmaking = findMatch(mode, {
      onSearching: () => {
        if (epoch === netEpoch) showWaiting(`Searching for a ${mode} opponent…`);
      },
      onMatched: (matchId) => {
        if (epoch !== netEpoch) return;
        matchmaking = null;
        track('online:matched');
        joinMatch(matchId);
      },
      onClose: () => {
        if (epoch !== netEpoch) return;
        matchmaking = null;
        if (online === null) showWaiting('Search ended — tap a duel button to retry.');
      },
    });
    loop.start();
  }

  /** How many times a dropped match connection is retried before giving up. */
  const RECONNECT_TRIES = 3;

  /** Connect to the paired match the matchmaker handed us (carrying our token + mode for rating).
   *  A mid-match drop auto-reconnects to the same match (the server re-seats us on our own side by
   *  token); a deliberate leave (epoch bump) or a finished match doesn't. */
  function joinMatch(matchId: string): void {
    const epoch = netEpoch;
    let attempts = 0;
    const connect = (): void => {
      if (epoch !== netEpoch) return; // we left in the meantime
      showWaiting(attempts === 0 ? 'Match found — joining…' : `Connection lost — reconnecting… (${attempts}/${RECONNECT_TRIES})`);
      online = connectMatch(matchUrl(matchId, { token: getToken(), mode: onlineMode }), {
        onState: (s) => {
          if (epoch !== netEpoch) return;
          attempts = 0; // a live state = a healthy link; future drops get fresh retries
          adoptServerState(s);
        },
        onStatus: (players, started) => {
          if (epoch === netEpoch) showWaiting(started ? null : `Waiting for opponent…\n(${players}/2)`);
        },
        onClose: () => {
          if (epoch !== netEpoch) return; // deliberate leave / a newer session took over
          online = null;
          if (state.phase !== 'final' && attempts < RECONNECT_TRIES) {
            attempts++;
            setTimeout(connect, 900 * attempts);
            return;
          }
          if (state.phase !== 'final') showWaiting('Disconnected.');
        },
      });
    };
    connect();
  }

  /** Launch the first-run interactive tutorial: a guided range session + the coach overlay. */
  function startTutorial(): void {
    feedback.unlock();
    leaveOnline();
    inTutorial = true;
    showWaiting(null);
    const seed = timeSeed();
    rng = createRng(seed);
    state = createInitialState(rng, 'easy', seed); // a real (gentle) vs-AI duel so DEFENSE is teachable
    lastDifficulty = 'easy';
    endShown = false;
    endScreen.hide();
    startScreen.hide();
    deckBuilder.hide();
    updateRangeHud();
    music.start(feedback.audioGraph());
    renderer.resize();
    track('tutorial:start');
    tutorial.start();
    loop.start();
  }

  /** Start an endless local RANGE session: hit balls from your bank, no opponent, no clock. */
  function startRange(): void {
    feedback.unlock();
    leaveOnline();
    showWaiting(null);
    track('match:start:range');
    const seed = timeSeed();
    rng = createRng(seed);
    state = { ...createInitialState(rng, 'normal', seed), mode: 'range', aiRival: false };
    rangeBalls = RANGE_BANK;
    endShown = false;
    endScreen.hide();
    startScreen.hide();
    deckBuilder.hide();
    updateRangeHud();
    music.start(feedback.audioGraph());
    renderer.resize();
    loop.start();
  }

  /** Start (or restart) a LOCAL match at the chosen difficulty with the chosen deck. */
  function startMatch(difficulty: Difficulty, deck?: DeckSelection): void {
    feedback.unlock();
    leaveOnline();
    showWaiting(null);
    updateRangeHud();
    track(`match:start:practice:${difficulty}`);
    lastDifficulty = difficulty;
    const seed = timeSeed();
    rng = createRng(seed);
    state = createInitialState(rng, difficulty, seed, deck);
    endShown = false;
    endScreen.hide();
    startScreen.hide();
    deckBuilder.hide();
    music.start(feedback.audioGraph()); // (re)start the music for the new match — resumes after an end.
    renderer.resize();
    loop.start();
  }

  // Paint a static field behind the start screen (after layout settles).
  requestAnimationFrame(() => {
    renderer.resize();
    renderer.render(state, 0);
    hud.update(state);
    hand.update(state);
    deckBuilder.hide();
    endScreen.hide();
    if (settings.tutorialDone) startScreen.show();
    else startTutorial(); // first run → straight into the interactive tutorial
  });

  // Resize handling that does NOT shake on mobile: the browser toolbar showing/hiding
  // fires a stream of `resize` events with a small height delta, which would re-letterbox
  // (rescale + recentre) the field every time → a visible shake on touch. Coalesce via rAF
  // and only re-fit on a WIDTH change (orientation) or a LARGE height change (rotation /
  // keyboard) — never the toolbar's small height wobble.
  let lastW = window.innerWidth;
  let lastH = window.innerHeight;
  let resizeScheduled = false;
  window.addEventListener('resize', () => {
    if (resizeScheduled) return;
    resizeScheduled = true;
    requestAnimationFrame(() => {
      resizeScheduled = false;
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (w !== lastW || Math.abs(h - lastH) > 140) {
        lastW = w;
        lastH = h;
        renderer.resize();
      }
    });
  });
  window.addEventListener('beforeunload', () => {
    loop.stop();
    music.stop();
    detachControls();
  });

  registerPwa();
}

bootstrap();
